
import { PureComponent } from 'react';

import {
    createRecordingDialogEvent,
    sendAnalytics
} from '../../../analytics';
import { MEDIA_TYPE } from '../../../base/media/constants';
import { getLocalParticipant } from '../../../base/participants';
import { getLocalTrack, getTrackState } from '../../../base/tracks/functions';
import { startLocalVideoRecording } from '../../actions.any';

type Props = {

    /**
     * The redux dispatch function.
     */
    dispatch: Function,

    /**
     * Invoked to obtain translated strings.
     */
    t: Function,

    /**
     * Whether there is a local microphone or live camera track to record when recording only self.
     */
    _hasLocalMedia: boolean,

    /**
     * Whether the camera is on. A self recording started without it is audio only for its whole length.
     */
    _hasLocalVideo: boolean,

    /**
     * The local participant object.
     */
    _localParticipant: Object
}

type State = {

    /**
     * Whether to record only local streams (self recording).
     */
    onlySelf: boolean
};

/**
 * Component for the local recording start dialog.
 */
class AbstractStartLocalRecordingDialog extends PureComponent<Props, State> {
    /**
     * Initializes a new {@code AbstractStartLocalRecordingDialog} instance.
     *
     * @inheritdoc
     */
    constructor(props: Props) {
        super(props);

        // Bind event handler so it is only bound once for every instance.
        this._onSubmit = this._onSubmit.bind(this);
        this._onSwitchChange = this._onSwitchChange.bind(this);

        this.state = {
            onlySelf: false // Default to recording only self
        };
    }

    _onSwitchChange: () => void;

    /**
     * Callback to handle switch setting change from the dialog.
     *
     * @returns {void}
     */
    _onSwitchChange() {
        this.setState({
            onlySelf: !this.state.onlySelf
        });
    }

    _onSubmit: () => boolean;

    /**
     * Starts a local recording session.
     *
     * @private
     * @returns {boolean} - True (to note that the modal should be closed).
     */
    _onSubmit() {
        const { _hasLocalMedia, dispatch } = this.props;
        const { onlySelf } = this.state;

        // Recording only self needs a local stream; without one LocalRecordingManager can only fail.
        if (onlySelf && !_hasLocalMedia) {
            return false;
        }

        sendAnalytics(
            createRecordingDialogEvent('start', 'confirm.button', {
                type: 'local',
                onlySelf
            })
        );

        dispatch(startLocalVideoRecording(onlySelf));

        return true;
    }

    /**
     * Renders the platform specific dialog content.
     *
     * @protected
     * @returns {React$Component}
     */
    _renderDialogContent: () => React$Component<*>;
}

/**
 * Maps (parts of) the Redux state to the associated props for the
 * {@code AbstractStartLocalRecordingDialog} component.
 *
 * @param {Object} state - The Redux state.
 * @private
 * @returns {{
 *     _hasLocalMedia: boolean,
 *     _hasLocalVideo: boolean,
 *     _localParticipant: Object
 * }}
 */
export function mapStateToProps(state: Object) {
    const tracks = getTrackState(state);
    const audioTrack = getLocalTrack(tracks, MEDIA_TYPE.AUDIO)?.mhTrack?.track;
    const videoTrack = getLocalTrack(tracks, MEDIA_TYPE.VIDEO)?.mhTrack?.track;

    return {
        // Same check LocalRecordingManager.startLocalRecording applies for a self recording.
        _hasLocalMedia: audioTrack?.readyState === 'live' || videoTrack?.readyState === 'live',
        _hasLocalVideo: videoTrack?.readyState === 'live',
        _localParticipant: getLocalParticipant(state)
    };
}

export default AbstractStartLocalRecordingDialog;
